/**
 * Chrono24 live-publish status — the single place the colour rules live.
 *
 * WHY THIS IS SHARED. Turning a CH24 toggle on only puts the listing into our
 * XML feed; Chrono24 pulls that feed on its own schedule and acknowledges
 * nothing. So "toggled on" is not "live", and the UI has to say which. Two
 * surfaces show it — the toggle row on the listing card and the tab dot on the
 * listing form — and if they derived it separately they would eventually
 * disagree. Backend contract: `listing_marketplace_state.publish_state`, set by
 * `chrono24:monitor-publish` (see retail-circle.com
 * .claude/features/chrono24-scraper.md).
 *
 * ⚠️ ORANGE MEANS SOMETHING NEW HERE. The form's tab dots have historically
 * used orange for "toggle off". For Chrono24 orange now means "queued, not yet
 * live", so an off Chrono24 dot renders NEUTRAL — otherwise the same colour
 * would mean two opposite things on the same screen.
 *
 * ⚠️ AN UNMONITORED ACCOUNT MUST LOOK EXACTLY AS IT DOES TODAY. The seed
 * listing link that makes monitoring possible is OPTIONAL, and the feed works
 * without it. When an account has no resolved dealer, there is no status to
 * report — so we fall back to the plain on/off rendering rather than showing a
 * permanently orange toggle, which would read as "stuck" when nothing is wrong.
 */

export const CH24_PUBLISH = {
  UNKNOWN: "unknown",
  PENDING: "pending",
  PUBLISHED: "published",
  DELISTED: "delisted",
};

/** Can this account's live status be observed at all? */
export const ch24CanMonitor = (account) => !!account?.ch24_customer_id;

/**
 * Presentation for one Chrono24 toggle row / tab dot.
 *
 * @param {object|null} params.account       StoreMarketplaceAccount
 * @param {boolean}     params.isListed      the toggle's own state
 * @param {object|null} params.state         listing_marketplace_state row
 * @returns {{tone:string, dot:string, pill:null|{tone:string,label:string,title:string}}}
 */
export function ch24PublishPresentation({ account, isListed, state }) {
  // Off: nothing is queued and nothing is live, on any account.
  if (!isListed) {
    return { tone: "default", dot: "neutral", pill: null };
  }

  // On, but this account cannot be monitored — render exactly as before.
  if (!ch24CanMonitor(account)) {
    return { tone: "default", dot: "on", pill: null };
  }

  switch (state?.publish_state) {
    case CH24_PUBLISH.PUBLISHED:
      return {
        tone: "active",
        dot: "on",
        pill: {
          tone: "published",
          label: "Live",
          title: "Seen on this dealer's Chrono24 inventory.",
        },
      };

    case CH24_PUBLISH.DELISTED:
      // The toggle is on and we DID see it live before, but it has since
      // disappeared. Distinct from pending: this one needs attention.
      return {
        tone: "pending",
        dot: "pending",
        pill: {
          tone: "error",
          label: "Removed",
          title:
            "This listing was live on Chrono24 but is no longer in the dealer's inventory.",
        },
      };

    // 'pending', 'unknown', or a row the monitor has not reached yet: queued
    // into the feed, not yet seen. Deliberately the same rendering — from the
    // merchant's point of view both mean "waiting on Chrono24".
    default:
      return {
        tone: "pending",
        dot: "pending",
        pill: {
          tone: "pending",
          label: "Pending",
          title:
            "Sent to Chrono24. The toggle turns green once we can see it live on your Chrono24 inventory (checked every 6 hours).",
        },
      };
  }
}

/**
 * Should the per-account state be fetched for this row?
 *
 * Only when the toggle is on AND the account is monitorable — an off or
 * unmonitored row has no status to show, so the request would be wasted. Same
 * shape as the WatchFinder pill's `enabled` guard on the listing card.
 */
export const ch24ShouldFetchState = (account, isListed) =>
  !!account?.id && !!isListed && ch24CanMonitor(account);
